import { ADMIN_CSM_ROLE, CSM_ROLE } from "@src/utility/constants/panel/common";
import { useContext, useRef } from "react";
import { ComponentSpinner } from "@src/@core/components/spinner";
import Avatar from "@components/ui-components/avatar";
import CustomTooltip from "@components/ui-components/tooltip";
import Flex from "@components/ui-components/flex";
import LoadingButton from "@components/ui-components/loading-button";
import { UserContext } from "@src/utility/context/user";
import { accessRoleByID } from "@src/utility/helpers/access";
import { format } from "date-fns";
import size from "lodash/size";
import { useTranslation } from "react-i18next";
import useCompanyCard from "./use-company-card";
import { useNavigate, useParams } from "react-router-dom";
const CompanyCard = ({ company, handleEdit, handleDelete }) => {
    const cardRef = useRef();
    const { userData:{ user }  } = useContext(UserContext);
    const { getStageHistory, historyData, loading } = useCompanyCard();

    const isCSM = [CSM_ROLE, ADMIN_CSM_ROLE].includes(user?.role ?? "");
    const canEdit = accessRoleByID({
        role      : user?.role ?? "",
        userId    : user?._id ?? "",
        resourceId: isCSM ? company?.csm?.id : company?.sdr?.id
    });
    const { platformId } = useParams();
    const { t } = useTranslation();
    const formatDate = (date) => (date ? format(new Date(date), "dd MMM yyyy") : "N/A");
    const navigate = useNavigate();
    return (
        <div
            ref={cardRef}
            className="border rounded p-1 bg-white cursor-move m-1 position-relative">
            <Flex className="border-bottom p-25" justify="between" align="center">
                <h5
                    className="text-primary cursor-pointer"
                    onClick={() => navigate(`/product/${platformId}/companies/${company._id}`)}>
                    {company.company_name}
                </h5>
                <Flex className="align-items-center">
                    <i
                        onClick={() => getStageHistory(company._id)}
                        id={`details-${company._id}`}
                        className="mdi mdi-information-outline text-info ms-1 cursor-pointer fs-3"/>
                    <CustomTooltip
                        trigger="click"
                        placement={"left"}
                        target={`details-${company._id}`}
                        content={
                            loading ? (
                                <div className="p-50">
                                    <ComponentSpinner />
                                </div>
                            ) : (
                                <div>
                                    <div className="fw-bold mb-1">{t("Stage History")}</div>
                                    {size(historyData["stage"]) ? (
                                        historyData["stage"].map((entry, idx) => (
                                            <div key={idx}>
                        • {entry.value} : {formatDate(entry.date)} | {entry.daysInStage} {"days"}
                                            </div>
                                        ))
                                    ) : (
                                        <div>{t("No stage history")}</div>
                                    )}

                                    <hr/>
                                    <div>
                                        <span className="fw-bold">{t("Time taken to onboard: ")}</span> {historyData?.time_taken_to_onboard ? `${historyData.time_taken_to_onboard} ${t("days")}` : t("N/A")}
                                    </div>
                                    <hr/>

                                    <div className="fw-bold mb-1">{t("CSM History")}</div>
                                    {size(historyData["csm"]) ? (
                                        historyData["csm"].map((entry, idx) => (
                                            <div key={idx}>
                        • {entry.value} : {formatDate(entry.date)}
                                            </div>
                                        ))
                                    ) : (
                                        <div>{t("No CSM history")}</div>
                                    )}
                                    
                                </div>
                            )
                        }/>
                    <Avatar
                        img={false}
                        color={"primary"}
                        initials
                        content={company.company_name}/>
                </Flex>
            </Flex>

            <Flex className="w-100 mt-2" justify="between" align="center">
                <Flex align="end">
                    <Flex column>
                        <span className="fw-bolder font-medium-2">{t("Products")}</span>
                        <span className="font-medium-1">{company.platform.name}</span>
                    </Flex>
                    <Flex align="end" column>
                        <span className="fw-bolder font-medium-2">{t("MRR")}</span>
                        <span className="font-medium-1">${company.mrr}</span>
                    </Flex>
                </Flex>
                <Flex align="center">
                    {canEdit && (
                        <LoadingButton
                            outline
                            color="info"
                            subject="companies"
                            action="update"
                            onClick={() => handleEdit(company)}
                            icon="pencil"/>
                    )}
                    <LoadingButton
                        outline
                        color="danger"
                        subject="companies"
                        action="delete"
                        icon="delete"
                        onClick={() => handleDelete(company._id)}/>
                </Flex>
            </Flex>
        </div>
    );
};

export default CompanyCard;
